import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { HelmetProvider } from 'react-helmet-async';
import RequireGuidedFlow from './components/guided/RequireGuidedFlow';
import { AuthProvider, useAuth } from './context/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
// 🚨 THE BROWSER HALF, WHICH DID NOT EXIST. usePushNotifications returns early
// off a native check, so a borrower on app.godoorly.com registered no device
// and received no notification — only the email. See the hook's header.
import { useWebPushNotifications } from './hooks/useWebPushNotifications';
import { useAppBadge } from './hooks/useAppBadge';
import { DoorlyProAuthProvider } from './context/DoorlyProAuthContext';
import { InvestorAuthProvider } from './context/InvestorAuthContext';
import InvestorPrivateRoute from './components/InvestorPrivateRoute';
import MarketLanding from './pages/market/MarketLanding';
import SampleTape from './pages/market/SampleTape';
import { SAMPLE_TAPE_PUBLISHED, SAMPLE_TAPE_PATH } from './constants/sampleTape';
import MarketRegister from './pages/market/MarketRegister';
import MarketLogin from './pages/market/MarketLogin';
import MarketForgotPassword from './pages/market/MarketForgotPassword';
import MarketResetPassword from './pages/market/MarketResetPassword';
import MarketDashboardInvestor from './pages/market/MarketDashboardInvestor';
import MarketPoolsList from './pages/market/MarketPoolsList';
import MarketPoolDetailInvestor from './pages/market/MarketPoolDetailInvestor';
import MarketBidsList from './pages/market/MarketBidsList';
import MarketDDRoom from './pages/market/MarketDDRoom';
import InvestorPortalDashboard from './pages/market/portal/InvestorPortalDashboard';
import InvestorPortalPools from './pages/market/portal/InvestorPortalPools';
import InvestorPortalBids from './pages/market/portal/InvestorPortalBids';
import InvestorPortalDocuments from './pages/market/portal/InvestorPortalDocuments';
import InvestorMessages from './pages/market/portal/InvestorMessages';
import InvestorAlerts from './pages/market/portal/InvestorAlerts';
import InvestorResources from './pages/market/portal/InvestorResources';
import InvestorUWGuide from './pages/market/portal/InvestorUWGuide';
import MarketMessagesAdmin from './pages/admin/market/MarketMessages';
import UnderwritingPortal from './pages/UnderwritingPortal';
import PriceTool from './pages/PriceTool';
import ProductDemo from './pages/ProductDemo';
import Forecast from './pages/Forecast';
import DoorlyProLogin from './pages/doorlypro/DoorlyProLogin';
// 🚨 PUBLIC. The signer is not a Doorly user and no session exists for them —
// the token routes the request and the identity check on the page is what
// establishes who is at the keyboard. See routes/brokerAuthorization.js.
import BorrowerAuthorizationSign from './pages/BorrowerAuthorizationSign';
import DoorlyProFirmPipeline from './pages/doorlypro/DoorlyProFirmPipeline';
import DoorlyProFirmTeam from './pages/doorlypro/DoorlyProFirmTeam';
import DoorlyProStart from './pages/doorlypro/DoorlyProStart';
import DoorlyProRegister from './pages/doorlypro/DoorlyProRegister';
import DoorlyProVerifyEmail from './pages/doorlypro/DoorlyProVerifyEmail';
import DoorlyProForgotPassword from './pages/doorlypro/DoorlyProForgotPassword';
import DoorlyProResetPassword from './pages/doorlypro/DoorlyProResetPassword';
import DoorlyProOnboarding from './pages/doorlypro/DoorlyProOnboarding';
import DoorlyProAgreement from './pages/doorlypro/DoorlyProAgreement';
import DoorlyProDashboard from './pages/doorlypro/DoorlyProDashboard';
import DoorlyProReferrals from './pages/doorlypro/DoorlyProReferrals';
import DoorlyProNewReferral from './pages/doorlypro/DoorlyProNewReferral';
import DoorlyProReferralDetail from './pages/doorlypro/DoorlyProReferralDetail';
import DoorlyProBorrowerJourney from './pages/doorlypro/DoorlyProBorrowerJourney';
import DoorlyProMessages from './pages/doorlypro/DoorlyProMessages';
import DoorlyProAlerts from './pages/doorlypro/DoorlyProAlerts';
import DoorlyProCompensation from './pages/doorlypro/DoorlyProCompensation';
import DoorlyProDocuments from './pages/doorlypro/DoorlyProDocuments';
import DoorlyProProfile from './pages/doorlypro/DoorlyProProfile';
import DoorlyProEligibility from './pages/doorlypro/DoorlyProEligibility';
import DoorlyProSubmissions from './pages/doorlypro/DoorlyProSubmissions';
import DoorlyProBorrowers from './pages/doorlypro/DoorlyProBorrowers';
import DoorlyProSubmissionFile from './pages/doorlypro/DoorlyProSubmissionFile';
import AgentUnreachable from './pages/AgentUnreachable';
import DoorlyProLanding from './pages/DoorlyProLanding';
import LandingPage from './pages/LandingPage';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DeleteAccount from './pages/DeleteAccount';
import PreQualification from './pages/PreQualification';
import ThankYou from './pages/ThankYou';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminOta from './pages/AdminOta';
import AdminEntry from './pages/AdminEntry';
import AdminAcceptInvite from './pages/AdminAcceptInvite';
import AdminResetPassword from './pages/AdminResetPassword';
import ResetPassword from './pages/ResetPassword';
import AdminSettingsPricing from './pages/AdminSettingsPricing';
import AdminMloLicences from './pages/AdminMloLicences';
import AdminSettingsUsers from './pages/AdminSettingsUsers';
import AdminDashboardPage from './pages/AdminDashboardPage';
import DashOverview from './pages/DashOverview';
import DashMarketing from './pages/DashMarketing';
import DashSales from './pages/DashSales';
import DashOperations from './pages/DashOperations';
import DashPartnerActivity from './pages/DashPartnerActivity';
import DashFunding from './pages/DashFunding';
import DashLoanPerformance from './pages/DashLoanPerformance';
import DashCustomerService from './pages/DashCustomerService';
import AdminTicketDetail from './pages/AdminTicketDetail';
import AdminLegal from './pages/AdminLegal';
import AdminBorrowers from './pages/AdminBorrowers';
import AdminLoanTape from './pages/AdminLoanTape';
import AdminLeadCaptures from './pages/AdminLeadCaptures';
import AdminHistoricals from './pages/AdminHistoricals';
import AdminModulesProperty from './pages/AdminModulesProperty';
import AdminModulesUnderwriting from './pages/AdminModulesUnderwriting';
import AdminFraudReview from './pages/AdminFraudReview';
import AdminModulesClosing from './pages/AdminModulesClosing';
import UnderwritingGuide from './pages/admin/UnderwritingGuide';
import AdminNoAccess from './pages/AdminNoAccess';
// 🔑 THE ONE ANSWER TO "MAY THIS ROLE SEE THIS PAGE", shared with the sidebar
// and the top tabs. See the module header for what it replaced.
import { maySeePath, landingPathFor, normaliseAdminPath } from './constants/staffNav';
import AdminBorrowerProfile from './pages/AdminBorrowerProfile';
import AdminReportsPipeline from './pages/AdminReportsPipeline';
import AdminReportsRevenue from './pages/AdminReportsRevenue';
import AdminReportsCalls from './pages/AdminReportsCalls';
import AdminSurveys from './pages/AdminSurveys';
// AdminMarketing import removed — the Marketing Overview launcher
// was retired in favor of landing directly on Content Queue (the
// /admin/marketing route now redirects there via <Navigate>).
import AdminContentQueue from './pages/AdminContentQueue';
import AdminAdCampaigns from './pages/AdminAdCampaigns';
import AdminPrOutreach from './pages/AdminPrOutreach';
import AdminSEO from './pages/AdminSEO';
import AdminFinance from './pages/AdminFinance';
import AdminWireRequests from './pages/AdminWireRequests';
import AdminPL from './pages/AdminPL';
import AdminReconciliation from './pages/AdminReconciliation';
import AdminSpending from './pages/AdminSpending';
import AdminRecipients from './pages/AdminRecipients';
import AdminPartnerFees from './pages/AdminPartnerFees';
import AdminAcquisition from './pages/AdminAcquisition';
import AdminWarehouse from './pages/AdminWarehouse';
import AdminSchedule from './pages/AdminSchedule';
import AdminPartnersDirectory from './pages/AdminPartnersDirectory';
import AdminPartnersAccounts from './pages/AdminPartnersAccounts';
import AdminPartnersReferrals from './pages/AdminPartnersReferrals';
import AdminSubmissionView from './pages/AdminSubmissionView';
import AdminPartnerEligibility from './pages/AdminPartnerEligibility';
import AdminPartnerTasks from './pages/AdminPartnerTasks';
import AdminLicenceChecks from './pages/AdminLicenceChecks';
import AdminManual from './pages/AdminManual';
import AdminPartnersMeetings from './pages/AdminPartnersMeetings';
import AdminPartnerLeads from './pages/AdminPartnerLeads';
// AdminTeam is the legacy team-management page (Owner/Admin/User user_type
// enum). Retired in favor of /admin/settings/users (5-role enum). The file
// stays on disk for now; nothing imports it. Route below redirects.
import MarketDashboard from './pages/MarketDashboard';
import MarketPools from './pages/MarketPools';
import MarketPoolDetail from './pages/MarketPoolDetail';
import MarketInvestors from './pages/MarketInvestors';
import MarketBids from './pages/MarketBids';
import MarketAlerts from './pages/MarketAlerts';
import AdminMessages from './pages/AdminMessages';
import AdminBorrowerMessages from './pages/AdminBorrowerMessages';
import AdminPartnerMessages from './pages/AdminPartnerMessages';
import AdminAlerts from './pages/AdminAlerts';
import AdminBorrowerAlerts from './pages/AdminBorrowerAlerts';
import AdminShowingRequests from './pages/AdminShowingRequests';
import AdminOfferRequests from './pages/AdminOfferRequests';
import AdminPartnerAlerts from './pages/AdminPartnerAlerts';
import Register from './pages/Register';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import MarketingTag from './components/MarketingTag';
import Settings from './pages/Settings';
import VerificationRoute from './components/VerificationRoute';
import Homes from './pages/Homes';
import Schedule from './pages/Schedule';
import HomeDetail from './pages/HomeDetail';
import UnderContract from './pages/UnderContract';
import OptionPeriod from './pages/OptionPeriod';
import LoanDocs from './pages/LoanDocs';
import ClosingDisclosures from './pages/ClosingDisclosures';
import FinalReview from './pages/FinalReview';
import Alerts from './pages/Alerts';
import GiftLetter from './pages/GiftLetter';
import VORForm from './pages/VORForm';
import VOMForm from './pages/VOMForm';
import RentFreeForm from './pages/RentFreeForm';
import DisclosurePortal from './pages/DisclosurePortal';
import OfferReview from './pages/OfferReview';
import CountersignPSA from './pages/CountersignPSA';
import ABPsaSigningPage from './pages/ABPsaSigningPage';
import Messages from './pages/Messages';
import SmartStart from './pages/SmartStart';
import { SurveyProvider } from './context/SurveyContext';
import SurveyOverlay, { PassiveFeedbackTab } from './components/SurveySystem';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// 🔑 LAZY, LIKE THE OTHER BORROWER PAGES. The shell is dead weight for every
// staff and partner session, and the flag is off by default — so the common
// case must not pay for it.
const GuidedFlowPage = React.lazy(() => import('./pages/GuidedFlowPage'));
// 🔑 The in-app document viewer (Jesse, 2026-10-28) — its own chunk; pdf.js itself loads only when a PDF is shown.
const DocumentViewer = React.lazy(() => import('./pages/DocumentViewer'));

function RequireAuth({ children }) {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return null;
  // 🔑 THE WHOLE DEEP LINK — path, query and hash — survives the login (Jesse, 2026-10-19; pages/Login.js).
  if (!user) return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
  return children;
}

// AdminOnly — gates a route to staff users with role === 'admin' (super
// admin). Distinct from RequireAuth (admin login) and from requireAdmin
// middleware on the backend (which lets any is_admin=1 staff through).
// Used for Finance + Marketing pages per explicit owner decision
// 2026-06-01: RE Desk + LO are walled off from money + marketing surfaces.
// localStorage-based so it works without re-fetching the user record; the
// sidebar already keys off the same source.
// ⚠️ TWO DIFFERENT STATES, TWO DIFFERENT ANSWERS. This used to render one
// "Admin access required" wall for both, with no route forward from either —
// which made even a correct direct link useless. On the native app, where there
// is no address bar and no back-to-a-URL, a dead end is genuinely terminal: it
// is half the reason staff could not reach /admin on a device at all.
//
//   NOT SIGNED IN to the admin session  -> that is a LOGIN, not a refusal.
//   SIGNED IN with the wrong role       -> a refusal, but with somewhere to go.
//
// ⚠️ STILL DISPLAY-LEVEL ONLY. This reads localStorage, which a determined user
// can edit — it decides what to RENDER, never what is permitted. Every admin
// endpoint behind it re-resolves the staff row and its roles server-side
// (backend middleware/adminAuth.js) on the separate admin token. Getting past
// this component grants nothing.
// 🔑 A BROKER SUBMISSION REFERENCE IN A BORROWER URL (/admin/borrowers/bs-11) FORWARDS TO THE SUBMISSION VIEW before
// the borrower page mounts (Jesse, 2026-11-02) — the profile would otherwise ask every borrower API for "bs-11".
function BorrowerRoute() {
  const { id } = useParams();
  const m = String(id || '').match(/^bs-(\d+)$/i);
  if (m) return <Navigate to={`/admin/partners/submissions/${Number(m[1])}`} replace />;
  return <AdminBorrowerProfile />;
}

function AdminOnly({ children }) {
  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem('doorly_admin_user') || 'null'); }
    catch { return null; }
  })();

  // No admin session on this device — send them to the login rather than a wall.
  // This is the common case for a staff member arriving from the borrower app's
  // Staff link, since the two sessions are separate.
  if (!adminUser) {
    return <Navigate to="/admin/login" replace />;
  }

  if (adminUser.role !== 'admin') {
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 24,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          Admin access required
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', maxWidth: 380 }}>
          This page is restricted to staff with the admin role.
          Contact a Doorly admin if you need access.
        </div>
        {/* ── 🔑 THE WAY FORWARD, AND IT USED TO BE A LITERAL ──────────────
            This read href="/admin/dashboard" with the note "the dashboard is
            where they can actually work". For Partner Success it is not: that
            page is hidden from their nav and refused by the server, so the only
            exit from this wall led to another one. landingPathFor() resolves
            the first page their own nav offers. */}
        <a
          href={landingPathFor(adminUser.role)}
          style={{
            marginTop: 16, fontSize: 13, fontWeight: 600, color: '#3a6ad9',
            textDecoration: 'none', padding: '8px 16px', borderRadius: 8,
            border: '1px solid #3a6ad9',
          }}
        >
          Go to dashboard
        </a>
      </div>
    );
  }
  return children;
}

function LastSeenTracker() {
  const { token } = useAuth();
  const location = useLocation();
  // Native push registration — no-op in browser/PWA. Lives here because this
  // component is mounted app-wide inside AuthProvider and already has `token`.
  usePushNotifications(token);
  // 🔑 AND THE BROWSER, WHICH IS WHERE MOST BORROWERS ARE. The two are mutually
  // exclusive by construction: the native hook returns early off !isNative and
  // this one returns early off isNative, so one user never registers twice.
  //
  // ⚠️ IT NEVER PROMPTS FROM HERE. It registers only when permission is ALREADY
  // granted; asking is a deliberate act behind a button, because a denial is
  // close to permanent and costs the borrower a trip into site settings.
  useWebPushNotifications(token);
  // Re-syncs the app-icon badge on mount and on every resume, so reading
  // alerts (or a bulk change made server-side) corrects the icon without
  // waiting for the next push to happen along.
  useAppBadge(token);
  useEffect(() => {
    if (!token) return;
    axios.put(`${API_URL}/api/users/me/last-seen`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [location.pathname, token]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// PwaManifestSwitcher — route-aware "Add to Home Screen" identity.
//
// The borrower "Doorly" PWA (manifest.json + apple meta in index.html) is the
// site-wide default and stays untouched. On iOS Safari, "Add to Home Screen"
// bookmarks the CURRENT url and reads apple-touch-icon / app-title /
// status-bar-style from the LIVE DOM at that moment — so while a staff user is
// on an /admin route we swap those tags (plus the <link rel=manifest>) to the
// DoorlyDesk identity. Jesse/Hannah on /admin get a DoorlyDesk icon that opens
// at /admin; borrowers on any other route keep the Doorly identity. We reuse
// the existing service-worker.js (it already covers /admin) — no second SW.
const PWA_BORROWER = {
  manifest: '/manifest.json',
  themeColor: '#1A3A6B',
  appTitle: 'Doorly',
  touchIcon: '/icon-192.png',
  statusBar: 'default',
};
const PWA_ADMIN = {
  manifest: '/manifest-admin.json',
  themeColor: '#111827',
  appTitle: 'DoorlyDesk',
  touchIcon: '/icons/doorly-admin-192.png',
  statusBar: 'black-translucent',
};
function applyPwaIdentity(id) {
  const set = (selector, attr, value) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };
  set('link[rel="manifest"]', 'href', id.manifest);
  set('meta[name="theme-color"]', 'content', id.themeColor);
  set('meta[name="apple-mobile-web-app-title"]', 'content', id.appTitle);
  set('link[rel="apple-touch-icon"]', 'href', id.touchIcon);
  set('meta[name="apple-mobile-web-app-status-bar-style"]', 'content', id.statusBar);
}
function PwaManifestSwitcher() {
  const { pathname } = useLocation();
  useEffect(() => {
    // desk.godoorly.com is entirely DoorlyDesk; on app.godoorly.com only the
    // /admin routes are. (The synchronous index.html swap already handles desk
    // at parse time; this is the SPA-navigation fallback.)
    const isDesk = typeof window !== 'undefined' && window.location.hostname === 'desk.godoorly.com';
    const isAdmin = isDesk || pathname === '/admin' || pathname.startsWith('/admin/');
    applyPwaIdentity(isAdmin ? PWA_ADMIN : PWA_BORROWER);
  }, [pathname]);
  // Restore the borrower default if this ever unmounts (e.g. HMR) so a stale
  // admin identity can't leak onto borrower routes.
  useEffect(() => () => applyPwaIdentity(PWA_BORROWER), []);
  return null;
}

// IS_DESK — true when served from the DoorlyDesk host. On desk.godoorly.com
// the admin app is mounted at prefix-stripped paths (/dashboard, /borrowers);
// on app.godoorly.com it stays under /admin/* during the transition.
const IS_DESK = typeof window !== 'undefined' && window.location.hostname === 'desk.godoorly.com';

// ─── DoorlyDesk admin routes — single source, rendered at one or two prefixes ──
// Add or change an admin route HERE only. adminRoutes(prefix) emits each at the
// given prefix; redirect entries use { to } whose target is prefixed too.
//   • app.godoorly.com → adminRoutes('/admin')         (legacy /admin/* paths)
//   • desk.godoorly.com → adminRoutes('') + adminRoutes('/admin')
//       so clean URLs (/dashboard) AND the internal navigate('/admin/...')
//       links both resolve. No borrower/market/public routes mount on desk, so
//       there are zero path collisions with /dashboard, /login, /market/*, etc.
const ADMIN_ROUTE_DEFS = [
  { p: '', el: <AdminEntry /> },
  { p: 'login', el: <AdminLogin /> },
  { p: 'accept-invite', el: <AdminAcceptInvite /> },
  { p: 'reset-password', el: <AdminResetPassword /> },
  { p: 'ota', el: <AdminOnly><AdminOta /></AdminOnly> },
  { p: 'settings/users', el: <AdminSettingsUsers /> },
  // The prime rate gate's release valve. Every prime_* alert links here; an
  // alert pointing at a page that does not exist is the same defect as a task
  // pointing at the registration route.
  { p: 'settings/pricing', el: <AdminSettingsPricing /> },
  // 🔑 M1 (2026-11-01): the MLO licences — who can be the MLO of record (routes/mloOfRecord; allow-listed writes).
  { p: 'settings/mlo-licences', el: <AdminMloLicences /> },
  // ⚠️ NO AdminOnly WRAPPER, DELIBERATELY. That guard reads doorly_admin_user out
  // of localStorage — a value any visitor can write — so it decides rendering and
  // grants nothing; its own source says as much. GET /api/manual is behind
  // requireAdmin (the adminAuth path, which resolves the staff row from the
  // database), and an unauthenticated visitor gets a 401 that the AdminLayout
  // interceptor turns into a redirect to the staff login. Wrapping in AdminOnly
  // as well would put the gate in two places that can disagree, and the weaker
  // of the two is the one people would then trust.
  //
  // Canonical URL is desk.godoorly.com/manual. This table is emitted at '' on
  // desk and at '/admin' on app, so /admin/manual also resolves on
  // app.godoorly.com — accepted fallout, not a second surface to maintain.
  { p: 'manual', el: <AdminManual /> },
  // 🔑 THE GUARD'S OWN DESTINATION. In UNGUARDED_ADMIN_PATHS, so it cannot
  // refuse itself and loop.
  { p: 'no-access', el: <AdminNoAccess /> },
  { p: 'dashboard', el: <DashOverview /> },
  { p: 'dashboard/overview', el: <DashOverview /> },
  { p: 'dashboard/marketing', el: <DashMarketing /> },
  { p: 'dashboard/sales', el: <DashSales /> },
  { p: 'dashboard/operations', el: <DashOperations /> },
  { p: 'dashboard/partner-activity', el: <DashPartnerActivity /> },
  { p: 'dashboard/funding', el: <DashFunding /> },
  { p: 'dashboard/loan-performance', el: <DashLoanPerformance /> },
  { p: 'dashboard/customer-service', el: <DashCustomerService /> },
  { p: 'dashboard/customer-service/tickets/:ref', el: <AdminTicketDetail /> },
  { p: 'legal', el: <AdminOnly><AdminLegal /></AdminOnly> },
  { p: 'dashboard/legacy', el: <AdminDashboardPage /> },
  { p: 'borrowers/leads', el: <AdminLeadCaptures /> },
  { p: 'borrowers', el: <AdminBorrowers /> },
  { p: 'borrowers/loan-tape', el: <AdminLoanTape /> },
  { p: 'borrowers/historicals', el: <AdminHistoricals /> },
  { p: 'modules/property', el: <AdminModulesProperty /> },
  { p: 'modules/underwriting', el: <AdminModulesUnderwriting /> },
  { p: 'modules/fraud-review', el: <AdminFraudReview /> },
  // 🔑 Section Review is retired (cleanup Phase 2): its regulated queues and Module reviews are on Waiting on staff.
  { p: 'modules/section-review', to: 'modules/underwriting' },
  { p: 'modules/closing', el: <AdminModulesClosing /> },
  { p: 'underwriting-guide', el: <UnderwritingGuide /> },
  { p: 'borrowers/messages', el: <AdminBorrowerMessages /> },
  { p: 'borrowers/alerts', el: <AdminBorrowerAlerts /> },
  { p: 'borrowers/showing-requests', el: <AdminShowingRequests /> },
  { p: 'borrowers/offer-requests', el: <AdminOfferRequests /> },
  { p: 'borrowers/:id', el: <BorrowerRoute /> },
  { p: 'reports', to: 'reports/pipeline' },
  { p: 'reports/pipeline', el: <AdminReportsPipeline /> },
  { p: 'reports/revenue', el: <AdminReportsRevenue /> },
  { p: 'reports/calls', el: <AdminReportsCalls /> },
  { p: 'surveys', el: <AdminOnly><AdminSurveys /></AdminOnly> },
  { p: 'marketing', to: 'marketing/content' },
  { p: 'marketing/content', el: <AdminOnly><AdminContentQueue /></AdminOnly> },
  { p: 'marketing/ads', el: <AdminOnly><AdminAdCampaigns /></AdminOnly> },
  { p: 'marketing/pr', el: <AdminOnly><AdminPrOutreach /></AdminOnly> },
  { p: 'marketing/seo', el: <AdminOnly><AdminSEO /></AdminOnly> },
  { p: 'finance', el: <AdminOnly><AdminFinance /></AdminOnly> },
  { p: 'finance/wire-requests', el: <AdminOnly><AdminWireRequests /></AdminOnly> },
  { p: 'finance/pl', el: <AdminOnly><AdminPL /></AdminOnly> },
  { p: 'finance/reconciliation', el: <AdminOnly><AdminReconciliation /></AdminOnly> },
  { p: 'finance/spending', el: <AdminOnly><AdminSpending /></AdminOnly> },
  { p: 'finance/recipients', el: <AdminOnly><AdminRecipients /></AdminOnly> },
  { p: 'finance/partner-fees', el: <AdminOnly><AdminPartnerFees /></AdminOnly> },
  { p: 'finance/acquisition', el: <AdminOnly><AdminAcquisition /></AdminOnly> },
  { p: 'finance/warehouse', el: <AdminOnly><AdminWarehouse /></AdminOnly> },
  { p: 'finance/wires', el: <AdminOnly><AdminWireRequests /></AdminOnly> },
  { p: 'schedule', el: <AdminSchedule /> },
  { p: 'partners/leads', el: <AdminPartnerLeads /> },
  { p: 'partners', to: 'partners/partners' },
  { p: 'partners/partners', el: <AdminPartnersDirectory /> },
  { p: 'partners/accounts', el: <AdminPartnersAccounts /> },
  { p: 'partners/referrals', el: <AdminPartnersReferrals /> },
  // 🔑 A broker submission before its borrower registers (forwards to the borrower file after) — Jesse 2026-11-02.
  { p: 'partners/submissions/:id', el: <AdminSubmissionView /> },
  { p: 'partners/tasks', el: <AdminPartnerTasks /> },
  { p: 'partners/licence-checks', el: <AdminLicenceChecks /> },
  { p: 'partners/eligibility', el: <AdminPartnerEligibility /> },
  { p: 'partners/meetings', el: <AdminPartnersMeetings /> },
  { p: 'team', to: 'settings/users' },
  { p: 'messages', el: <AdminMessages /> },
  { p: 'partners/messages', el: <AdminPartnerMessages /> },
  { p: 'partners/alerts', el: <AdminPartnerAlerts /> },
  { p: 'alerts', el: <AdminAlerts /> },
  { p: 'market', to: 'market/dashboard' },
  { p: 'market/dashboard', el: <MarketDashboard /> },
  { p: 'market/pools', el: <MarketPools /> },
  { p: 'market/pools/:id', el: <MarketPoolDetail /> },
  { p: 'market/loans', to: 'market/pools' },
  { p: 'market/funding', to: 'market/bids' },
  { p: 'market/investors', el: <MarketInvestors /> },
  { p: 'market/bids', el: <MarketBids /> },
  { p: 'market/messages', el: <MarketMessagesAdmin /> },
  { p: 'market/alerts', el: <MarketAlerts /> },
  { p: 'legacy', el: <AdminDashboard /> },
  { p: 'coming-soon', el: (
    <div style={{ paddingTop: '64px', paddingLeft: '220px', minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#9ca3af' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚧</div>
        <p style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>Coming Soon</p>
        <p style={{ fontSize: '13px', marginTop: '4px' }}>This section is under construction.</p>
      </div>
    </div>
  ) },
  { p: 'countersign-psa', el: <CountersignPSA /> },
];

// ─────────────────────────────────────────────────────────────────────────────
// 🚨 A PAGE A ROLE MAY NOT SEE DOES NOT RENDER.
//
// The sidebar has filtered its items by role for a long time, and its own
// comment recorded the limit: "Defensive only — the route still renders; this
// just makes sure nav doesn't suggest surfaces the user can't act on." So a
// Partner Success rep could not NAVIGATE to the admin dashboard and landed on
// it anyway — because the login redirect sent every role to /admin/dashboard —
// where the server correctly refused six of its eight widgets and the page
// showed an error box. Jesse: "If they do not/should not have access, they
// should not be able to view or see the page at all."
//
// 🔑 ONE RULE, THREE SURFACES. maySeePath() in constants/staffNav.js is derived
// from the nav itself, so the sidebar, the top tabs and this guard cannot
// disagree — and a section whose role rule changes takes its tab and its route
// with it.
//
// ⚠️ THIS GRANTS NOTHING AND IS NOT THE GATE. It reads the role out of
// localStorage, which any visitor can write. backend/constants/staffRoles.js is
// default-closed and refuses the DATA independently; this decides what the
// product OFFERS, which is the half that was producing the confusion.
//
// ⚠️ AND IT REDIRECTS RATHER THAN BLANKING. A refusal with no way forward
// replaces a dead affordance with a dead end. landingPathFor() resolves the
// first page the role's own nav offers, and /admin/no-access explains it when
// they arrived by URL.
// ─────────────────────────────────────────────────────────────────────────────
function StaffPageGuard({ children }) {
  const location = useLocation();
  const role = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('doorly_admin_user') || 'null');
      return u && u.role ? u.role : null;
    } catch { return null; }
  })();

  // 🔑 NO SESSION, NO ROLE QUESTION. The pages that create a session are in
  // UNGUARDED_ADMIN_PATHS, and everything else behind AdminLayout already
  // redirects an unauthenticated visitor to the staff login on its first 401.
  // Guessing here would bounce somebody mid-login.
  if (!role) return children;
  if (maySeePath(role, location.pathname)) return children;

  const home = landingPathFor(role);
  const normalised = normaliseAdminPath(location.pathname);
  // ⚠️ THE EXPLAINER WHEN THERE IS NOWHERE ELSE, AND WHEN THEY TYPED THE URL.
  // Silently landing somebody on a different page than the one they asked for
  // reads as the app losing their click; naming the refusal does not.
  return <Navigate to="/admin/no-access" replace state={{ from: normalised, home }} />;
}

function adminRoutes(prefix) {
  const at = (suffix) => {
    const joined = `${prefix}/${suffix}`.replace(/\/+/g, '/').replace(/\/$/, '');
    return joined === '' ? '/' : joined;
  };
  return ADMIN_ROUTE_DEFS.map(({ p, el, to }) => (
    <Route
      key={`${prefix}|${p}`}
      path={at(p)}
      // 🔑 WRAPPED HERE, WHICH IS THE ONE PLACE EVERY ADMIN ROUTE PASSES
      // THROUGH — at both prefixes, for every page, including ones added
      // tomorrow. Per-route wrapping is how a page gets missed.
      //
      // ⚠️ REDIRECT ENTRIES ARE WRAPPED TOO. A `to` entry is how /admin/reports
      // becomes /admin/reports/pipeline, and an unguarded redirect would be a
      // two-step route into a page the guard would otherwise refuse.
      element={<StaffPageGuard>{to ? <Navigate to={at(to)} replace /> : el}</StaffPageGuard>}
    />
  ));
}

function AppRoutes() {
  // desk.godoorly.com: admin-only route table (clean + legacy paths), no
  // borrower/market/public routes — so prefix-stripped paths can't collide.
  if (IS_DESK) {
    return (
      <>
        <ScrollToTop />
        <PwaManifestSwitcher />
        <Routes>
          {adminRoutes('')}
          {adminRoutes('/admin')}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    );
  }
  return (
    <>
    <ScrollToTop />
    <PwaManifestSwitcher />
    <LastSeenTracker />
    {/* 🚨 THE GOOGLE ADS TAG, SCOPED. Mounted here and NOT in the IS_DESK tree
        above — see components/MarketingTag.jsx. It renders nothing; it decides,
        on every navigation, whether the tag is in the document at all. */}
    <MarketingTag />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/underwriting" element={<UnderwritingPortal />} />
      <Route path="/price-tool" element={<PriceTool />} />
      <Route path="/product-demo" element={<ProductDemo />} />
      <Route path="/forecast" element={<Forecast />} />
      <Route path="/privacy" element={<Privacy />} />
      {/* ⚠️ DRAFT PENDING COUNSEL REVIEW. To unpublish, delete this one line —
          the page keeps building and nothing else references the route. */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/pre-qualify" element={<PreQualification />} />
      {/* ── 🚨 /apply IS GUARDED TOO, AND IT IS THE ROUTE THAT MATTERED MOST ──
          🚨 "ROUTED TO THE SHELL FROM LOGIN" HAD NEVER WORKED FOR THE BORROWERS
          IT IS FOR. utils/getRedirectPath sends a borrower whose application is
          not_started or in_progress to /apply (or /apply?step=N) — and only this
          route was unguarded, so exactly the borrowers mid-application landed on
          the legacy page outside the shell. Dashboard.js forwards them there
          too. Only a SUBMITTED borrower reached /dashboard and got redirected
          in, which is the one group that needs it least.

          🔑 FOUND BY LOGGING IN FROM A SECOND BROWSER in the e2e suite: the
          borrower resumed at "Step 2 of 3" on the old application page instead
          of "Step 2 of 8" in the shell. */}
      {/* 🔑 cleanup Phase 2: the standalone application form is gone; /apply (and /apply?step=N from old links,
          utils/getRedirectPath and Register) is the shell. */}
      <Route path="/apply" element={<RequireAuth><Navigate to="/apply/guided" replace /></RequireAuth>} />
      <Route path="/thank-you" element={<ThankYou />} />
      {adminRoutes('/admin')}
      {/* DoorlyMarket — investor-facing portal (public) */}
      <Route path="/market" element={<MarketLanding />} />
      {/* 🚨 Taken down until the tape is Texas-only (Hannah, 2026-11-03): constants/sampleTape.js is the switch. */}
      <Route path={SAMPLE_TAPE_PATH} element={SAMPLE_TAPE_PUBLISHED ? <SampleTape /> : <Navigate to="/market" replace />} />
      <Route path="/market/register" element={<MarketRegister />} />
      <Route path="/market/login" element={<MarketLogin />} />
      <Route path="/market/forgot-password" element={<MarketForgotPassword />} />
      <Route path="/market/reset-password" element={<MarketResetPassword />} />
      <Route path="/market/dashboard" element={<InvestorPrivateRoute><MarketDashboardInvestor /></InvestorPrivateRoute>} />
      <Route path="/market/pools" element={<InvestorPrivateRoute><MarketPoolsList /></InvestorPrivateRoute>} />
      <Route path="/market/pools/:id" element={<InvestorPrivateRoute><MarketPoolDetailInvestor /></InvestorPrivateRoute>} />
      <Route path="/market/bids" element={<InvestorPrivateRoute><MarketBidsList /></InvestorPrivateRoute>} />
      <Route path="/market/bids/:bidId/dd-room" element={<InvestorPrivateRoute><MarketDDRoom /></InvestorPrivateRoute>} />
      {/* Investor portal — wrapped in InvestorPortalLayout (auth + nav) */}
      <Route path="/market/portal" element={<Navigate to="/market/portal/dashboard" replace />} />
      <Route path="/market/portal/dashboard" element={<InvestorPortalDashboard />} />
      <Route path="/market/portal/pools" element={<InvestorPortalPools />} />
      <Route path="/market/portal/bids" element={<InvestorPortalBids />} />
      <Route path="/market/portal/messages" element={<InvestorMessages />} />
      <Route path="/market/portal/alerts" element={<InvestorAlerts />} />
      <Route path="/market/portal/resources" element={<InvestorResources />} />
      <Route path="/market/portal/resources/underwriting-guide" element={<InvestorUWGuide />} />
      <Route path="/market/portal/documents" element={<InvestorPortalDocuments />} />
      {/* Public status-by-email page RETIRED (P0): it let anyone read a
          borrower's loan stage + adverse/fraud flags by typing an email.
          The authenticated dashboard is the status home. */}
      <Route path="/status" element={<Navigate to="/login" replace />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* ── 🚨 THE GUIDED SHELL'S OWN ROUTE ─────────────────────────────────
          Not wrapped in RequireGuidedFlow — that guard redirects INTO here, and
          wrapping the destination in its own guard is a redirect loop. */}
      {/* 🔑 THE SHELL AND ITS IN-SHELL VIEWS (Jesse, 2026-10-19): /apply/guided (the current step), /documents,
          /messages, /account and /saved — every borrower route below redirects a guided borrower here until the
          step-8 decision (components/guided/RequireGuidedFlow). */}
      <Route path="/apply/guided/*" element={<RequireAuth><React.Suspense fallback={null}><GuidedFlowPage /></React.Suspense></RequireAuth>} />
      <Route path="/dashboard" element={<RequireAuth><RequireGuidedFlow><Dashboard /></RequireGuidedFlow></RequireAuth>} />
      {/* 🔑 A permanent redirect (the classic Verification page is gone): Documents, an old ?addAssets=1 link too (VerificationRoute; the post-decision Add assets is removed, 2026-11-02). */}
      <Route path="/verification" element={<RequireAuth><VerificationRoute /></RequireAuth>} />
      {/* 🔑 NOT GUARDED (2026-10-16): Documents is the guided borrower's home for the application — pages/Documents
          renders the guided page for them (Action needed, every upload, Edit Application) and the classic page for
          everyone else, so a /documents#action-needed link from an email lands where it points. */}
      <Route path="/documents" element={<RequireAuth><RequireGuidedFlow><Documents /></RequireGuidedFlow></RequireAuth>} />
      {/* 🔑 View without the Browser plugin (iOS 1.0.2), or when the web cannot open a tab — pages/DocumentViewer. */}
      <Route path="/documents/view/:id" element={<RequireAuth><React.Suspense fallback={null}><DocumentViewer /></React.Suspense></RequireAuth>} />
      <Route path="/settings"  element={<RequireAuth><RequireGuidedFlow><Settings /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/homes" element={<RequireAuth><RequireGuidedFlow><Homes /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/schedule" element={<RequireAuth><RequireGuidedFlow><Schedule /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/homes/:id" element={<RequireAuth><RequireGuidedFlow><HomeDetail /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/under-contract" element={<RequireAuth><RequireGuidedFlow><UnderContract /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/option-period"  element={<RequireAuth><RequireGuidedFlow><OptionPeriod /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/loan-docs"            element={<RequireAuth><RequireGuidedFlow><LoanDocs /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/closing-disclosures"  element={<RequireAuth><RequireGuidedFlow><ClosingDisclosures /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/final-review"         element={<RequireAuth><RequireGuidedFlow><FinalReview /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/portal/documents" element={<Navigate to="/documents" replace />} />
      <Route path="/alerts" element={<RequireAuth><RequireGuidedFlow><Alerts /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/messages" element={<RequireAuth><RequireGuidedFlow><Messages /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/smartstart" element={<RequireAuth><RequireGuidedFlow><SmartStart /></RequireGuidedFlow></RequireAuth>} />
      <Route path="/gift-letter" element={<GiftLetter />} />
      <Route path="/vor" element={<VORForm />} />
      <Route path="/vom" element={<VOMForm />} />
      <Route path="/rent-free" element={<RentFreeForm />} />
      <Route path="/disclosure/:token" element={<DisclosurePortal />} />
      <Route path="/offer-review/:token" element={<OfferReview />} />
      <Route path="/sign-ab-psa" element={<ABPsaSigningPage />} />
      {/* 🔑 /pro AND /pro/referrals NEVER EXISTED (Jesse/Hannah 2026-11-03): two partner offer emails linked them
          (backend/utils/partnerOfferEmails), so the already-sent links 404'd. frontend/vercel.json 301s them on every host
          but the Help Center; these routes do the same in the SPA (local dev, previews, an in-app navigation). Both go to the
          referrals page (Hannah 2026-11-03 evening): every /pro link ever sent was "View Your Referrals". */}
      <Route path="/pro" element={<Navigate to="/doorlypro/referrals" replace />} />
      <Route path="/pro/referrals" element={<Navigate to="/doorlypro/referrals" replace />} />
      {/* DoorlyPro Partner Portal */}
      <Route path="/doorlypro" element={<DoorlyProLanding />} />
      <Route path="/authorize" element={<BorrowerAuthorizationSign />} />
      <Route path="/doorlypro/login" element={<DoorlyProLogin />} />
      <Route path="/doorlypro/firm/pipeline" element={<DoorlyProFirmPipeline />} />
      <Route path="/doorlypro/firm/team" element={<DoorlyProFirmTeam />} />
      <Route path="/doorlypro/start" element={<DoorlyProStart />} />
      <Route path="/doorlypro/register" element={<DoorlyProRegister />} />
      <Route path="/doorlypro/register-account" element={<DoorlyProRegister />} />
      <Route path="/doorlypro/verify-email" element={<DoorlyProVerifyEmail />} />
      <Route path="/doorlypro/forgot-password" element={<DoorlyProForgotPassword />} />
      <Route path="/doorlypro/reset-password" element={<DoorlyProResetPassword />} />
      <Route path="/doorlypro/onboarding" element={<DoorlyProOnboarding />} />
      <Route path="/doorlypro/onboarding/agreement" element={<DoorlyProAgreement />} />
      <Route path="/doorlypro/dashboard" element={<DoorlyProDashboard />} />
      <Route path="/doorlypro/referrals" element={<DoorlyProReferrals />} />
      <Route path="/doorlypro/referrals/new" element={<DoorlyProNewReferral />} />
      <Route path="/doorlypro/referrals/:id" element={<DoorlyProReferralDetail />} />
      <Route path="/doorlypro/borrower-journey" element={<DoorlyProBorrowerJourney />} />
      <Route path="/doorlypro/messages" element={<DoorlyProMessages />} />
      <Route path="/doorlypro/alerts" element={<DoorlyProAlerts />} />
      <Route path="/doorlypro/compensation" element={<DoorlyProCompensation />} />
      <Route path="/doorlypro/documents" element={<DoorlyProDocuments />} />
      <Route path="/doorlypro/profile" element={<DoorlyProProfile />} />
      <Route path="/doorlypro/eligibility" element={<DoorlyProEligibility />} />
      <Route path="/doorlypro/submissions" element={<DoorlyProSubmissions />} />
      <Route path="/doorlypro/borrowers" element={<DoorlyProBorrowers />} />
      {/* 🔑 A submitted file's own read-only page (LO plan v7, 2026-10-28). The PDF 1003 review page went with the format. */}
      <Route path="/doorlypro/submissions/:id" element={<DoorlyProSubmissionFile />} />
      {/* The §2.5 trigger. The entry point is hidden for borrowers with no agent
          (see the Dashboard), so reaching this by URL is the only way a
          self-registered borrower lands here — the page says so plainly. */}
      <Route path="/agent-unreachable" element={<AgentUnreachable />} />
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-black mb-4">404</h1>
              <p className="text-gray-600 text-lg mb-6">Page not found.</p>
              <a href="/" className="doorly-btn-primary inline-block">Back to Home</a>
            </div>
          </div>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <DoorlyProAuthProvider>
        <InvestorAuthProvider>
          <AuthProvider>
            <Router>
              <SurveyProvider>
                <AppRoutes />
                <SurveyOverlay />
                <PassiveFeedbackTab />
              </SurveyProvider>
            </Router>
          </AuthProvider>
        </InvestorAuthProvider>
      </DoorlyProAuthProvider>
    </HelmetProvider>
  );
}

export default App;
